import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import FileIcon from '@atlaskit/icon/glyph/file';
import { Inline } from '@atlaskit/primitives';
import {
	MenuSection,
	MenuSectionHeading,
} from '@atlassian/navigation-system/side-nav/menu-section';
import { useMemo } from 'react';
import type { PreloadedQuery } from 'react-relay';
import { graphql, usePreloadedQuery } from 'react-relay';

import { createFlowRoute } from '../../../../routes/utils';
import { useCreateFlowSession } from '../../hooks/use-create-flow';
import { useRepositoryModal } from '../../page-home/components/repository-modal';
import { deserializeAdditionalInfo } from '../../utils/additional-info-json';

import type { flowsQuery } from './__generated__/flowsQuery.graphql';
import { NavigationItem } from './navigation-item';

const FlowItems = ({ queryRef }: { queryRef: PreloadedQuery<flowsQuery> }) => {
	const data = usePreloadedQuery<flowsQuery>(
		graphql`
			query flowsQuery($creator: String!, $cloudId: ID!) {
				devai_flowSessionsByCreatorAndCloudId(creator: $creator, cloudId: $cloudId)
					@optIn(to: "DevAiFlowSessionsByCreatorAndCloudId") {
					id
					sessionARI
					status
					additionalInfoJSON
				}
			}
		`,
		queryRef,
	);

	const inProgressFlows = useMemo(() => {
		return (
			data.devai_flowSessionsByCreatorAndCloudId?.filter(
				// TODO: replace this with a filter through the query
				(flow) => flow?.status === 'IN_PROGRESS',
			) ?? []
		);
	}, [data.devai_flowSessionsByCreatorAndCloudId]);

	return (
		<>
			{inProgressFlows.map((flow) => {
				const { branchName } = deserializeAdditionalInfo(flow?.additionalInfoJSON ?? '{}');
				return (
					<NavigationItem
						key={flow?.id}
						id={flow?.id}
						flowARI={flow?.sessionARI}
						icon={<FileIcon label="Flow" size="medium" />}
						status={flow?.status ?? ''}
						href={createFlowRoute(flow?.id ?? '')}
					>
						{branchName ?? `New flow - ${flow?.id}`}
					</NavigationItem>
				);
			})}
		</>
	);
};

export const Flows = ({ queryRef }: { queryRef?: PreloadedQuery<flowsQuery> | null }) => {
	const [createFlow, isPending] = useCreateFlowSession();

	const { openModal, closeModal, RepositoryModal } = useRepositoryModal({
		branchName: `NO-ISSUE-${Date.now()}`,
		onConfirm: (repositoryUrl, branchName, prompt) => {
			createFlow({
				repoUrl: repositoryUrl,
				branchName,
				prompt,
				onSuccess: () => {
					closeModal();
				},
			});
		},
		isSubmitPending: isPending,
	});

	return (
		<MenuSection>
			<MenuSectionHeading>
				<Inline space="space.100" spread="space-between" alignBlock="center">
					Flows
					<IconButton
						onClick={openModal}
						label="New flow"
						icon={AddIcon}
						appearance="subtle"
						spacing="compact"
					/>
				</Inline>
			</MenuSectionHeading>
			{queryRef && <FlowItems queryRef={queryRef} />}

			{RepositoryModal}
		</MenuSection>
	);
};
